// @ts-nocheck
import { showToast } from "../store/features/Notifications/notificationsSlice";
import {networkHealthOf, type CallNetworkHealth} from './callNetworkHealth';
import logger from '../services/logger';
import type { SessionTimings } from './iceCandidateHandler';
import { getAudioFlags } from '../services/abTest/variant';
import { getActiveAudioContext } from './backgroundedAudioWorklet';

// Type definitions
interface AudioHealthOptions {
    pollMs?: number;
    windowSeconds?: number;
}

interface LongTaskEntry {
    t: number;
    d: number;
}

interface AudioHealthState {
    last: {
        track_ts: number | null;
        track_totalSamplesDuration: number | null;
        remote_ts: number | null;
        remote_packetsLost: number | null;
        remote_packetsReceived: number | null;
        remote_totalRoundTripTime: number | null;
        remote_roundTripTimeMeasurements: number | null;
        inbound_ts: number | null;
        inbound_packetsReceived: number | null;
        inbound_packetsLost: number | null;
        inbound_bytesReceived: number | null;
        inbound_nackCount: number | null;
        inbound_jitterBufferDelay: number | null;
        inbound_jitterBufferEmittedCount: number | null;
        outbound_ts: number | null;
        outbound_packetsSent: number | null;
        outbound_nackCount: number | null;
        outbound_retransmittedPacketsSent: number | null;
    };
    codecLoggedAt: number | null;
    longTaskMsInWindow: number;
    longTaskTimestamps: LongTaskEntry[];
    consecutiveStuckCount: number;
    consecutiveOutboundStuckCount: number;
    /** True once the first inbound packets are seen — guards the initial-flow callback. */
    initialFlowDetected: boolean;
    /** True while inbound audio is stuck — ensures the stopped callback fires exactly once. */
    inboundFlowStopped: boolean;
    /** Polls in a row where jitterBufferMs exceeded the path-specific warn threshold. */
    consecutiveJitterBufferOver: number;
    /** Date.now() of the last jitter-buffer toast; gates the next one via cooldown. */
    lastJitterToastAt: number | null;
    /** Polls in a row where inbound loss rate exceeded the toast threshold. */
    consecutiveLossOver: number;
    /** Date.now() of the last packet-loss toast; gates the next one via cooldown. */
    lastLossToastAt: number | null;
}

type SeverityLevel = 'ok' | 'warn' | 'error';

export type {CallNetworkHealth} from './callNetworkHealth';

// Custom interfaces for WebRTC stats since TypeScript's built-in ones are restrictive
interface CustomTrackStats {
    type: string;
    timestamp: number;
    kind?: string;
    totalSamplesDuration?: number;
}

interface CustomRemoteInboundRtpStats {
    type: string;
    timestamp: number;
    kind?: string;
    packetsLost?: number;
    packetsReceived?: number;
    jitter?: number;
    roundTripTime?: number;
    roundTripTimeMeasurements?: number;
    totalRoundTripTime?: number;
    codecId?: string;
}

interface CustomOutboundRtpStats {
    type: string;
    timestamp: number;
    kind?: string;
    packetsSent?: number;
    nackCount?: number;
    retransmittedPacketsSent?: number;
    codecId?: string;
}

interface CustomInboundRtpStats {
    type: string;
    timestamp: number;
    kind?: string;
    packetsReceived?: number;
    bytesReceived?: number;
    packetsLost?: number;
    jitter?: number;
    nackCount?: number;
    fecPacketsReceived?: number;
    fecPacketsDiscarded?: number;
    jitterBufferDelay?: number;
    jitterBufferEmittedCount?: number;
    audioLevel?: number;
    totalAudioEnergy?: number;
    codecId?: string;
}

interface CustomCodecStats {
    type: string;
    id: string;
    mimeType?: string;
    clockRate?: number;
    channels?: number;
    sdpFmtpLine?: string;
}

// Redux dispatch type (you might need to import the actual type from your store)
interface DispatchFunction {
    (action: any): void;
}

/**
 * Create an audio health monitor that surfaces user-facing messages.
 * @param {RTCPeerConnection} pc - your peer connection (with a local audio sender).
 * @param {AudioHealthOptions} opts - configuration options
 * @param {DispatchFunction} dispatch - Redux dispatch function
 * @param {SessionTimings} timings - call timing markers (mutated in place)
 * @param {string} callId - identifier for log correlation
 * @param {(flowing: boolean) => void} onInboundFlowChange - optional callback fired when inbound
 *   audio starts flowing for the first time (true), stops after being stuck (false), or recovers
 *   after a stopped event (true). Useful for playing/stopping a comfort tone.
 * @param {(network: CallNetworkHealth) => void} onNetworkChange - optional callback with the
 *   network state after every poll, for the call bubble (D-INCALL-02/S11, S12).
 */
export function createAudioHealthMonitor(
    pc: RTCPeerConnection,
    opts: AudioHealthOptions = {},
    dispatch: DispatchFunction,
    timings: SessionTimings = {},
    callId = 'unknown',
    onInboundFlowChange?: (flowing: boolean) => void,
    onNetworkChange?: (network: CallNetworkHealth) => void,
) {
    const pollMs = opts.pollMs ?? 2000;
    const windowSeconds = opts.windowSeconds ?? 10;
    let pollCount = 0;
    const periodicLogIntervalMs = 30_000; // log a stats summary every 30 s
    let lastPeriodicLogAt = Date.now();

    // Network-type tagging: snapshot the Network Information API once on start
    // and refresh on `change`. Chrome populates `type` (wifi/cellular/ethernet)
    // and `effectiveType` (2g/3g/4g/slow-2g); Firefox + Safari expose only
    // `effectiveType` or nothing. Spread these into every audio_* log entry so
    // Kibana can bucket WiFi vs. cellular medians without needing a second
    // correlated index.
    const networkTypeLogging = getAudioFlags().networkTypeLogging;
    const getNetSnapshot = () => {
        if (!networkTypeLogging) return {};
        const c = (navigator as any).connection;
        if (!c) {
            return {
                network_type: null,
                network_effectiveType: null,
                network_downlink: null,
                network_rtt: null,
                network_saveData: null,
            };
        }
        return {
            network_type: c.type ?? null,
            network_effectiveType: c.effectiveType ?? null,
            network_downlink: c.downlink ?? null,
            network_rtt: c.rtt ?? null,
            network_saveData: c.saveData ?? null,
        };
    };
    let netSnapshot = getNetSnapshot();
    const onNetChange = () => {
        const prev = netSnapshot;
        netSnapshot = getNetSnapshot();
        if (
            prev.network_type !== netSnapshot.network_type ||
            prev.network_effectiveType !== netSnapshot.network_effectiveType
        ) {
            logger.info('Network type changed during call', {
                source: 'sip',
                event: 'audio_network_change',
                callId,
                from: prev,
                to: netSnapshot,
            });
        }
    };
    const connectionApi = networkTypeLogging ? (navigator as any).connection : null;
    if (networkTypeLogging) {
        try {
            connectionApi?.addEventListener?.('change', onNetChange);
        } catch (e) {
            // Safari/Firefox may throw on non-standard API — ignore.
        }
    }

    // Local logger wrapper that merges `netSnapshot` into every ctx object.
    // All `logger.*` calls inside `poll()` and `stop()` go through this so we
    // don't have to spread the snapshot at every single call site. When
    // networkTypeLogging is off, `netSnapshot` is an empty object and `logNet`
    // degrades to a pass-through for plain `logger.*`.
    const logNet = {
        debug: (msg: string, ctx: any = {}) => logger.debug(msg, { ...ctx, ...netSnapshot }),
        info:  (msg: string, ctx: any = {}) => logger.info (msg, { ...ctx, ...netSnapshot }),
        warn:  (msg: string, ctx: any = {}) => logger.warn (msg, { ...ctx, ...netSnapshot }),
        error: (msg: string, ctx: any = {}) => logger.error(msg, { ...ctx, ...netSnapshot }),
    };

    const state: AudioHealthState = {
        last: {
            track_ts: null,
            track_totalSamplesDuration: null,
            remote_ts: null,
            remote_packetsLost: null,
            remote_packetsReceived: null,
            remote_totalRoundTripTime: null,
            remote_roundTripTimeMeasurements: null,
            inbound_ts: null,
            inbound_packetsReceived: null,
            inbound_packetsLost: null,
            inbound_bytesReceived: null,
            inbound_nackCount: null,
            inbound_jitterBufferDelay: null,
            inbound_jitterBufferEmittedCount: null,
            outbound_ts: null,
            outbound_packetsSent: null,
            outbound_nackCount: null,
            outbound_retransmittedPacketsSent: null,
        },
        codecLoggedAt: null,
        longTaskMsInWindow: 0,
        longTaskTimestamps: [],
        consecutiveStuckCount: 0,
        consecutiveOutboundStuckCount: 0,
        consecutiveJitterBufferOver: 0,
        lastJitterToastAt: null,
        consecutiveLossOver: 0,
        lastLossToastAt: null,
        initialFlowDetected: false,
        inboundFlowStopped: false,
    };

    // ---- Long task observer (main-thread overload signal)
    if ('PerformanceObserver' in window) {
        const po = new PerformanceObserver((list) => {
            const now = performance.now();
            for (const e of list.getEntries()) {
                // e.duration is ms of a >50ms long task
                state.longTaskMsInWindow += e.duration;
                state.longTaskTimestamps.push({ t: now, d: e.duration });
            }
            // drop old entries
            const cutoff = now - windowSeconds * 1000;
            while (state.longTaskTimestamps.length && state.longTaskTimestamps[0].t < cutoff) {
                state.longTaskMsInWindow -= state.longTaskTimestamps[0].d;
                state.longTaskTimestamps.shift();
            }
        });
        try {
            po.observe({ entryTypes: ['longtask'] });
        } catch (error) {
            logNet.warn('Long task observer not supported', { source: 'sip', event: 'longtask_observer_error' });
        }
    }

    function formatPct(x: number): string {
        return `${Math.round(x * 100)}%`;
    }

    async function poll(): Promise<void> {
        pollCount++;
        try {
            const stats = await pc.getStats();

            // We'll accumulate the best-matching reports
            let track: CustomTrackStats | null = null;
            let remoteInbound: CustomRemoteInboundRtpStats | null = null;
            let outbound: CustomOutboundRtpStats | null = null;
            let inbound: CustomInboundRtpStats | null = null;
            const codecs = new Map<string, CustomCodecStats>();

            stats.forEach((r: RTCStats) => {
                const stat = r as any;

                if (stat.type === 'track' && stat.kind === 'audio' && stat.totalSamplesDuration != null) {
                    track = stat as CustomTrackStats;
                }
                if (stat.type === 'remote-inbound-rtp' && stat.kind === 'audio') {
                    remoteInbound = stat as CustomRemoteInboundRtpStats;
                }
                if (stat.type === 'outbound-rtp' && stat.kind === 'audio') {
                    outbound = stat as CustomOutboundRtpStats;
                }
                if (stat.type === 'inbound-rtp' && stat.kind === 'audio') {
                    inbound = stat as CustomInboundRtpStats;
                }
                if (stat.type === 'codec') {
                    codecs.set(stat.id, stat as CustomCodecStats);
                }
            });

            const messages: string[] = [];
            let severity: SeverityLevel = 'ok';

            // ---- 0) Codec — log once at call start (first poll where we have an outbound codecId)
            if (!state.codecLoggedAt && outbound?.codecId) {
                const codec = codecs.get(outbound.codecId);
                if (codec) {
                    state.codecLoggedAt = Date.now();
                    logNet.info('Audio codec negotiated', {
                        source: 'sip',
                        event: 'audio_codec',
                        callId,
                        mimeType: codec.mimeType,
                        clockRate: codec.clockRate,
                        channels: codec.channels,
                        sdpFmtpLine: codec.sdpFmtpLine,
                    });
                }
            }

            // ---- 1) Processing real-time ratio (CPU / pipeline pressure proxy)
            if (track && state.last.track_ts != null && state.last.track_totalSamplesDuration != null) {
                const dtWall = (track.timestamp - state.last.track_ts) / 1000; // seconds
                const dtSamples = (track.totalSamplesDuration! - state.last.track_totalSamplesDuration) || 0; // seconds
                const realTimeRatio = dtSamples / Math.max(dtWall, 1e-3); // 1.0 == real-time

                // Heuristics
                if (realTimeRatio < 0.85) {
                    severity = 'error';
                    messages.push(`Your audio is slightly delayed (${Math.round(realTimeRatio*100)}% of real-time). Closing other apps may help keep sound smooth. `);
                } else if (realTimeRatio < 0.95) {
                    if (severity !== 'error') {
                        severity = 'warn';
                    }
                    messages.push(`Your audio is under load  (${Math.round(realTimeRatio*100)}% of real-time). Closing other apps may help improve sound quality. `);
                }
            }

            if (track) {
                state.last.track_ts = track.timestamp;
                state.last.track_totalSamplesDuration = track.totalSamplesDuration ?? 0;
            }

            // ---- 2) What the far end experiences: loss, jitter & RTT
            if (remoteInbound) {
                if (state.last.remote_ts != null) {
                    const dPacketsLost = (remoteInbound.packetsLost ?? 0) - (state.last.remote_packetsLost ?? 0);
                    const dPacketsRecv  = (remoteInbound.packetsReceived ?? 0) - (state.last.remote_packetsReceived ?? 0);
                    const total = Math.max(dPacketsLost + dPacketsRecv, 1);
                    const lossRate = dPacketsLost / total; // 0..1

                    const jitterMs = (remoteInbound.jitter ?? 0) * 1000; // seconds -> ms

                    // RTT: use instantaneous roundTripTime if available, else derive from cumulative counters
                    const rttMs = remoteInbound.roundTripTime != null
                        ? remoteInbound.roundTripTime * 1000
                        : (() => {
                            const dTotal = (remoteInbound.totalRoundTripTime ?? 0) - (state.last.remote_totalRoundTripTime ?? 0);
                            const dMeasurements = (remoteInbound.roundTripTimeMeasurements ?? 0) - (state.last.remote_roundTripTimeMeasurements ?? 0);
                            return dMeasurements > 0 ? (dTotal / dMeasurements) * 1000 : null;
                        })();

                    // Average RTT for the whole call so far
                    const avgRttMs = remoteInbound.roundTripTimeMeasurements
                        ? ((remoteInbound.totalRoundTripTime ?? 0) / remoteInbound.roundTripTimeMeasurements) * 1000
                        : null;

                    logNet.debug('Remote inbound RTP stats', {
                        source: 'sip',
                        event: 'audio_stats_remote_inbound',
                        callId,
                        lossRate: Math.round(lossRate * 1000) / 1000,
                        jitterMs: Math.round(jitterMs * 10) / 10,
                        rttMs: rttMs != null ? Math.round(rttMs) : null,
                        avgRttMs: avgRttMs != null ? Math.round(avgRttMs) : null,
                        roundTripTimeMeasurements: remoteInbound.roundTripTimeMeasurements,
                    });

                    if (lossRate > 0.08 || jitterMs > 30) {
                        severity = 'error';
                        messages.push(`Network issue: loss ${formatPct(lossRate)}${jitterMs ? `, jitter ${Math.round(jitterMs)}ms` : ''}${rttMs != null ? `, RTT ${Math.round(rttMs)}ms` : ''}. Call quality may be affected.`);
                    } else if (lossRate > 0.03 || jitterMs > 20) {
                        if (severity !== 'error') {
                            severity = 'warn';
                        }
                        messages.push(`Severe network issue: loss ${formatPct(lossRate)}${jitterMs ? `, jitter ${Math.round(jitterMs)}ms` : ''}${rttMs != null ? `, RTT ${Math.round(rttMs)}ms` : ''}. Call quality may be affected.`);
                    } else if (rttMs != null && rttMs > 300) {
                        if (severity !== 'error') {
                            severity = 'warn';
                        }
                        messages.push(`High network latency: RTT ${Math.round(rttMs)}ms. Call quality may be affected.`);
                    }
                }

                state.last.remote_ts = remoteInbound.timestamp;
                state.last.remote_packetsLost = remoteInbound.packetsLost ?? 0;
                state.last.remote_packetsReceived = remoteInbound.packetsReceived ?? 0;
                state.last.remote_totalRoundTripTime = remoteInbound.totalRoundTripTime ?? 0;
                state.last.remote_roundTripTimeMeasurements = remoteInbound.roundTripTimeMeasurements ?? 0;
            }

            // ---- 2.4) Log OUTBOUND stats (what we're SENDING to remote)
            if (outbound) {
                if (state.last.outbound_ts != null) {
                    const dPacketsSent = (outbound.packetsSent ?? 0) - (state.last.outbound_packetsSent ?? 0);
                    const dNack = (outbound.nackCount ?? 0) - (state.last.outbound_nackCount ?? 0);
                    const dRetransmitted = (outbound.retransmittedPacketsSent ?? 0) - (state.last.outbound_retransmittedPacketsSent ?? 0);

                    logNet.debug('Outbound RTP stats', {
                        source: 'sip',
                        event: 'audio_stats_outbound',
                        callId,
                        packetsSent: outbound.packetsSent,
                        dPacketsSent,
                        dNack,
                        dRetransmitted,
                        consecutiveOutboundStuckCount: state.consecutiveOutboundStuckCount,
                        sinceConfirmedMs: timings.confirmedAt ? Date.now() - timings.confirmedAt : null,
                        sinceDtlsConnectedMs: timings.dtlsConnectedAt ? Date.now() - timings.dtlsConnectedAt : null,
                    });

                    if (dPacketsSent === 0) {
                        state.consecutiveOutboundStuckCount++;
                        if (state.consecutiveOutboundStuckCount >= 2) {
                            logNet.warn(`Not sending audio to remote for ${state.consecutiveOutboundStuckCount * 2}s`, {
                                source: 'sip',
                                event: 'audio_stuck_outbound',
                                stuckSeconds: state.consecutiveOutboundStuckCount * 2,
                            });
                        }
                    } else {
                        if (state.consecutiveOutboundStuckCount > 0) {
                            logNet.info(`Outbound audio resumed after ${state.consecutiveOutboundStuckCount * 2}s`, {
                                source: 'sip',
                                event: 'audio_resumed_outbound',
                                stuckSeconds: state.consecutiveOutboundStuckCount * 2,
                            });
                        }
                        state.consecutiveOutboundStuckCount = 0;
                    }
                }

                state.last.outbound_ts = outbound.timestamp;
                state.last.outbound_packetsSent = outbound.packetsSent ?? 0;
                state.last.outbound_nackCount = outbound.nackCount ?? 0;
                state.last.outbound_retransmittedPacketsSent = outbound.retransmittedPacketsSent ?? 0;
            } else {
                logNet.warn('No outbound-rtp stats available', { source: 'sip', event: 'audio_stats_outbound_missing' });
            }

            // ---- 2.5) CRITICAL: Check if we're RECEIVING audio from remote
            if (inbound) {
                if (state.last.inbound_ts != null) {
                    const dPacketsReceived = (inbound.packetsReceived ?? 0) - (state.last.inbound_packetsReceived ?? 0);
                    const dBytesReceived = (inbound.bytesReceived ?? 0) - (state.last.inbound_bytesReceived ?? 0);
                    const dNack = (inbound.nackCount ?? 0) - (state.last.inbound_nackCount ?? 0);

                    // Jitter buffer delay: average ms audio spends in the jitter buffer per poll interval
                    const dJitterDelay = (inbound.jitterBufferDelay ?? 0) - (state.last.inbound_jitterBufferDelay ?? 0);
                    const dJitterEmitted = (inbound.jitterBufferEmittedCount ?? 0) - (state.last.inbound_jitterBufferEmittedCount ?? 0);
                    const jitterBufferMs = dJitterEmitted > 0 ? (dJitterDelay / dJitterEmitted) * 1000 : null;

                    // Audio level: 0.0–1.0 (indicates silence vs. speech)
                    const audioLevel = inbound.audioLevel ?? null;

                    // Track first received packet timestamp for latency measurement
                    if (dPacketsReceived > 0 && !timings.firstRxAt) {
                        const firstRxAt = Date.now();
                        timings.firstRxAt = firstRxAt;
                        logNet.info('First inbound RTP received', {
                            source: 'sip',
                            event: 'first_rx',
                            sinceConfirmedMs: timings.confirmedAt ? firstRxAt - timings.confirmedAt : null,
                            sinceIceConnectedMs: timings.iceConnectedAt ? firstRxAt - timings.iceConnectedAt : null,
                            sinceDtlsConnectedMs: timings.dtlsConnectedAt ? firstRxAt - timings.dtlsConnectedAt : null,
                        });
                    }

                    logNet.debug('Inbound RTP stats', {
                        source: 'sip',
                        event: 'audio_stats_inbound',
                        callId,
                        packetsReceived: inbound.packetsReceived,
                        bytesReceived: inbound.bytesReceived,
                        packetsLost: inbound.packetsLost,
                        jitter: inbound.jitter != null ? Math.round(inbound.jitter * 1000) : null, // ms
                        jitterBufferMs: jitterBufferMs != null ? Math.round(jitterBufferMs * 10) / 10 : null,
                        dNack,
                        fecPacketsReceived: inbound.fecPacketsReceived,
                        fecPacketsDiscarded: inbound.fecPacketsDiscarded,
                        audioLevel: audioLevel != null ? Math.round(audioLevel * 1000) / 1000 : null,
                        dPacketsReceived,
                        dBytesReceived,
                        consecutiveStuckCount: state.consecutiveStuckCount,
                    });

                    // Initial inbound flow detection — fires onInboundFlowChange(true) once the
                    // first time we see packets increasing. This cancels any comfort tone started
                    // at confirmed time that covers the ICE/DTLS setup silence (~1–3 s).
                    if (!state.initialFlowDetected && dPacketsReceived > 0) {
                        state.initialFlowDetected = true;
                        logNet.info('Initial inbound audio flow detected', {
                            source: 'sip',
                            event: 'audio_initial_flow_detected',
                            callId,
                            sinceConfirmedMs: timings.confirmedAt ? Date.now() - timings.confirmedAt : null,
                        });
                        onInboundFlowChange?.(true);
                    }

                    // CRITICAL: Detect if packets stopped coming in
                    if (dPacketsReceived === 0 && dBytesReceived === 0) {
                        state.consecutiveStuckCount++;

                        if (state.consecutiveStuckCount >= 2) { // Stuck for 4+ seconds
                            severity = 'error';
                            messages.push(`⚠️ RECEIVING NO AUDIO from remote! (stuck for ${state.consecutiveStuckCount * 2}s)`);
                            logNet.warn(`No inbound RTP for ${state.consecutiveStuckCount * 2}s`, {
                                source: 'sip',
                                event: 'audio_stuck_inbound',
                                stuckSeconds: state.consecutiveStuckCount * 2,
                                consecutiveStuckCount: state.consecutiveStuckCount,
                            });
                            // Fire stopped callback exactly once when crossing the threshold
                            if (state.consecutiveStuckCount === 2 && !state.inboundFlowStopped) {
                                state.inboundFlowStopped = true;
                                onInboundFlowChange?.(false);
                            }
                        } else {
                            if (severity !== 'error') {
                                severity = 'warn';
                            }
                            messages.push('Audio reception stalled');
                        }
                    } else {
                        // Inbound flow recovered — fire callback once
                        if (state.inboundFlowStopped) {
                            state.inboundFlowStopped = false;
                            logNet.info(`Inbound audio flow recovered after ${state.consecutiveStuckCount * 2}s`, {
                                source: 'sip',
                                event: 'audio_flow_recovered',
                                callId,
                                stuckSeconds: state.consecutiveStuckCount * 2,
                            });
                            onInboundFlowChange?.(true);
                        } else if (state.consecutiveStuckCount > 0) {
                            logNet.info(`Inbound audio resumed after ${state.consecutiveStuckCount * 2}s`, {
                                source: 'sip',
                                event: 'audio_resumed_inbound',
                                stuckSeconds: state.consecutiveStuckCount * 2,
                            });
                        }
                        state.consecutiveStuckCount = 0;
                    }

                    // Jitter buffer too large → noticeable audio delay.
                    // Worklet path (Chrome OS variant=d ctx-destination) has a
                    // stable ~170-180ms baseline because Web Audio's render
                    // quantum adds drain timing the receiver's adaptive buffer
                    // reads as "slow consumer" and grows past the 120ms target.
                    // Raise the threshold to 200ms on that path and require two
                    // consecutive polls + a 60s cooldown before toasting so a
                    // stable above-threshold call doesn't toast every 2s.
                    const isWorkletPath = getActiveAudioContext() != null;
                    const jitterWarnThresholdMs = isWorkletPath ? 200 : 150;
                    if (jitterBufferMs != null && jitterBufferMs > jitterWarnThresholdMs) {
                        state.consecutiveJitterBufferOver++;
                        const now = Date.now();
                        const cooledDown = state.lastJitterToastAt == null
                            || now - state.lastJitterToastAt > 60_000;
                        if (state.consecutiveJitterBufferOver >= 2 && cooledDown) {
                            if (severity !== 'error') {
                                severity = 'warn';
                            }
                            messages.push(`Audio buffer delay: ${Math.round(jitterBufferMs)}ms. You may notice a slight echo or delay.`);
                            state.lastJitterToastAt = now;
                        }
                    } else {
                        state.consecutiveJitterBufferOver = 0;
                    }

                    // Inbound packet loss this poll. Was comparing
                    // inbound.packetsLost against state.last.remote_packetsLost
                    // (outbound side reported by RTCP) which produced spurious
                    // double-digit loss spikes on calls with zero real inbound
                    // loss. Track inbound_packetsLost separately.
                    const dPacketsLost = Math.max(
                        0,
                        (inbound.packetsLost ?? 0) - (state.last.inbound_packetsLost ?? 0),
                    );
                    const dReceivedClamped = Math.max(0, dPacketsReceived);
                    const totalForLoss = dReceivedClamped + dPacketsLost;
                    const inboundLossRate = totalForLoss > 0 ? dPacketsLost / totalForLoss : 0;

                    // Min-sample guard: skip classification when the poll's
                    // denominator is tiny (DTX / sparse intervals) — 1 lost
                    // out of 5 received is 17% but not actionable.
                    // Two-poll debounce + 60s cooldown matches the jitter
                    // toast policy: a single 2s burst won't toast.
                    if (totalForLoss >= 20 && inboundLossRate > 0.08) {
                        state.consecutiveLossOver++;
                        const now = Date.now();
                        const cooledDown = state.lastLossToastAt == null
                            || now - state.lastLossToastAt > 60_000;
                        if (state.consecutiveLossOver >= 2 && cooledDown) {
                            if (severity !== 'error') {
                                severity = 'warn';
                            }
                            messages.push(`High packet loss receiving audio: ${formatPct(inboundLossRate)}`);
                            state.lastLossToastAt = now;
                        }
                    } else {
                        state.consecutiveLossOver = 0;
                    }

                    // Augment the inbound stats log with the actual delta +
                    // rate the toast logic sees, so a Kibana query can
                    // verify the calculation without inferring it.
                    logNet.debug('Inbound RTP loss delta', {
                        source: 'sip',
                        event: 'audio_inbound_loss_delta',
                        callId,
                        dPacketsLost,
                        dPacketsReceivedClamped: dReceivedClamped,
                        totalForLoss,
                        inboundLossRate: Math.round(inboundLossRate * 1000) / 1000,
                        consecutiveLossOver: state.consecutiveLossOver,
                    });
                }

                state.last.inbound_ts = inbound.timestamp;
                state.last.inbound_packetsReceived = inbound.packetsReceived ?? 0;
                state.last.inbound_packetsLost = inbound.packetsLost ?? 0;
                state.last.inbound_bytesReceived = inbound.bytesReceived ?? 0;
                state.last.inbound_nackCount = inbound.nackCount ?? 0;
                state.last.inbound_jitterBufferDelay = inbound.jitterBufferDelay ?? 0;
                state.last.inbound_jitterBufferEmittedCount = inbound.jitterBufferEmittedCount ?? 0;
            } else {
                logNet.warn('No inbound-rtp stats available', { source: 'sip', event: 'audio_stats_inbound_missing' });
            }

            // ---- 2.6) TX/RX delta — compare outbound sent vs. what RTCP remote reports
            // outbound.packetsSent = what we sent from our side
            // remoteInbound.packetsReceived + packetsLost = what the remote accounted for via RTCP
            // Gap > 0 = we sent packets that remote hasn't accounted for yet (RTCP lag or true loss)
            if (outbound && remoteInbound && state.last.outbound_ts != null && state.last.remote_ts != null) {
                const dTxSent     = (outbound.packetsSent ?? 0) - (state.last.outbound_packetsSent ?? 0);
                const dRxReceived = (remoteInbound.packetsReceived ?? 0) - (state.last.remote_packetsReceived ?? 0);
                const dRxLost     = (remoteInbound.packetsLost ?? 0) - (state.last.remote_packetsLost ?? 0);
                const dRxTotal    = dRxReceived + dRxLost;  // packets remote accounted for this interval
                const txRxGap     = dTxSent - dRxTotal;     // unaccounted packets
                const txRxGapPct  = dTxSent > 0 ? txRxGap / dTxSent : 0;

                logNet.debug('RTP TX/RX delta', {
                    source: 'sip',
                    event: 'audio_tx_rx_delta',
                    callId,
                    dTxSent,
                    dRxReceived,
                    dRxLost,
                    dRxTotal,
                    txRxGap,
                    txRxGapPct: Math.round(txRxGapPct * 1000) / 1000,
                });

                // Warn only when we have enough traffic and the gap is meaningful
                // Positive gap > 5% with at least 10 packets sent = likely real loss, not just RTCP lag
                if (dTxSent >= 10 && txRxGapPct > 0.05) {
                    logNet.warn('TX/RX gap suggests packet loss in transit', {
                        source: 'sip',
                        event: 'audio_tx_rx_gap_warn',
                        callId,
                        dTxSent,
                        dRxReceived,
                        dRxLost,
                        dRxTotal,
                        txRxGap,
                        txRxGapPct: Math.round(txRxGapPct * 1000) / 1000,
                    });
                }
            }

            // ---- 3) Main-thread long tasks in the recent window (indirect CPU signal)
            const longTaskShare = Math.min(state.longTaskMsInWindow / (windowSeconds * 1000), 1); // 0..1
            if (longTaskShare > 0.25) {
                severity = 'error';
                messages.push(`Your device is under high load (${formatPct(longTaskShare)}), which may affect connection quality.`);
            } else if (longTaskShare > 0.1) {
                if (severity !== 'error') {
                    severity = 'warn';
                }
                messages.push(`Your device is under heavy use (${formatPct(longTaskShare)}), which may affect connection quality. `);
            }

            // ---- 4) Fallback hint if we have outbound but no remote stats yet (call not connected)
            if (!remoteInbound && outbound && (outbound.packetsSent ?? 0) < 10) {
                messages.push('Connecting… checking your audio settings.  ');
            }

            // ---- Periodic health summary (every 30 s regardless of severity)
            const now = Date.now();
            if (now - lastPeriodicLogAt >= periodicLogIntervalMs) {
                lastPeriodicLogAt = now;

                // Compute average RTT for summary
                const avgRttMs = remoteInbound?.roundTripTimeMeasurements
                    ? Math.round(((remoteInbound.totalRoundTripTime ?? 0) / remoteInbound.roundTripTimeMeasurements) * 1000)
                    : null;

                // Jitter buffer average over full call
                const jbAvgMs = inbound?.jitterBufferEmittedCount
                    ? Math.round(((inbound.jitterBufferDelay ?? 0) / inbound.jitterBufferEmittedCount) * 1000 * 10) / 10
                    : null;

                // Cumulative TX/RX gap over entire call
                const totalTxSent  = state.last.outbound_packetsSent ?? 0;
                const totalRxAccounted = (state.last.remote_packetsReceived ?? 0) + (state.last.remote_packetsLost ?? 0);
                const cumulativeTxRxGap    = totalTxSent - totalRxAccounted;
                const cumulativeTxRxGapPct = totalTxSent > 0
                    ? Math.round((cumulativeTxRxGap / totalTxSent) * 1000) / 1000
                    : null;

                logNet.info('Audio health periodic summary', {
                    source: 'sip',
                    event: 'audio_health_summary',
                    callId,
                    severity,
                    inboundPackets: state.last.inbound_packetsReceived,
                    outboundPackets: state.last.outbound_packetsSent,
                    avgRttMs,
                    jitterBufferAvgMs: jbAvgMs,
                    inboundJitterMs: inbound?.jitter != null ? Math.round(inbound.jitter * 1000) : null,
                    outboundNackTotal: state.last.outbound_nackCount,
                    inboundNackTotal: state.last.inbound_nackCount,
                    cumulativeTxRxGap,
                    cumulativeTxRxGapPct,
                    consecutiveStuckCount: state.consecutiveStuckCount,
                    consecutiveOutboundStuckCount: state.consecutiveOutboundStuckCount,
                    longTaskSharePct: Math.round(Math.min(state.longTaskMsInWindow / (windowSeconds * 1000), 1) * 100),
                    sinceConfirmedMs: timings.confirmedAt ? now - timings.confirmedAt : null,
                });
            }

            onNetworkChange?.(networkHealthOf(pc.connectionState, severity));

            // ---- Render banner
            if(severity !== 'ok') {
                renderBanner(messages, severity);
            }
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : String(e);
            logNet.warn(`Audio health monitor error: ${errorMessage}`, { source: 'sip', event: 'audio_health_monitor_error' });
            renderBanner([`Audio health monitor error: ${errorMessage}`], 'warn');
        }
    }

    function renderBanner(lines: string[], severity: SeverityLevel): void {
        dispatch(showToast({
            type: severity === 'error' ? 'error' : severity === 'warn' ? 'warning' : 'info',
            description: lines.join(' '),
            duration: 10000,
        }));
    }

    const id = setInterval(poll, pollMs);
    return {
        stop: () => {
            clearInterval(id);
            try {
                connectionApi?.removeEventListener?.('change', onNetChange);
            } catch (e) {
                // Ignore — see addEventListener try/catch above.
            }
            logNet.info('Audio health monitor stopped', {
                source: 'sip',
                event: 'audio_health_stopped',
                callId,
                totalPolls: pollCount,
                finalInboundPackets: state.last.inbound_packetsReceived,
                finalOutboundPackets: state.last.outbound_packetsSent,
                sinceConfirmedMs: timings.confirmedAt ? Date.now() - timings.confirmedAt : null,
            });
        },
        /** Run an immediate poll outside the regular interval (e.g. on tab focus). */
        runNow: () => { poll(); },
    };
}
